import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";

import { verifyEmail as verifyEmailApi } from "@/features/auth/api/authApi";
import { formatAuthError } from "@/features/auth/model/useAuth";
import { broadcastEmailVerified } from "@/features/auth/model/verifiedChannel";

type Status =
  | { kind: "pending" }
  | { kind: "success"; email: string }
  | { kind: "error"; message: string };

export function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>({ kind: "pending" });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setStatus({ kind: "pending" });
    verifyEmailApi(token)
      .then((data) => {
        if (cancelled) return;
        broadcastEmailVerified(data.email);
        setStatus({ kind: "success", email: data.email });
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus({ kind: "error", message: formatAuthError(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6 rounded-lg border border-border bg-surface p-8 shadow-card">
        <div className="space-y-1 text-center">
          <Link
            to="/"
            className="inline-block text-2xl font-semibold tracking-display-tight text-copy transition-colors hover:text-primary"
            aria-label="Go to OneShift home"
          >
            OneShift
          </Link>
          <p className="text-sm text-copy-muted">Verify your email</p>
        </div>

        {!token && (
          <p className="text-center text-sm text-destructive-foreground">
            No verification token provided.
          </p>
        )}

        {token && status.kind === "pending" && (
          <p className="text-center text-sm text-copy-muted">Verifying your email…</p>
        )}

        {status.kind === "success" && (
          <div className="space-y-4 text-center">
            <div className="flex flex-col items-center gap-3 rounded-md border border-success bg-success-light p-6">
              <CheckCircle2 className="h-12 w-12 text-success-foreground" />
              <p className="text-lg font-semibold text-success-foreground">Email verified!</p>
              <p className="text-sm text-success-foreground">
                {status.email} is now verified. You can close this tab and continue
                in the window where you signed up.
              </p>
            </div>
            <Link
              to="/login?verified=1"
              className="inline-block w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
            >
              Or sign in here
            </Link>
          </div>
        )}

        {status.kind === "error" && (
          <div className="space-y-4 text-center">
            <div className="flex flex-col items-center gap-3 rounded-md border border-destructive bg-destructive-light p-6">
              <XCircle className="h-12 w-12 text-destructive-foreground" />
              <p className="text-lg font-semibold text-destructive-foreground">
                Verification failed
              </p>
              <p className="text-sm text-destructive-foreground">{status.message}</p>
            </div>
            <Link
              to="/login"
              className="inline-block text-sm font-medium text-primary hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
